import React, { useState, useEffect } from "react";
import { Company, CreateCompanyData } from "../../store/types";
import { validateField, formatPhone } from "../../hooks/validation";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import CustomInputText from "../CustomInput/CustomInputText";
import CustomInputMask from "../CustomInput/CustomInputMask";
import { StyledIconLine } from "../StyledIcon/StyledIcon";
import { DangerText } from "../../styles/Typography";
import {
  IconCompany,
  IconMail,
  IconPhone,
  IconCompanyFilled,
  IconMailFilled,
  IconPhoneFilled,
} from "../../assets/icons/Icon";
import { Column, SmallColumn } from "./Dialogs.styles";

interface DialogProps {
  visible: boolean;
  onHide: () => void;
  dialogType: string;
  company?: Company;
  handleAddCompany?: (company: CreateCompanyData) => Promise<boolean>;
  handleEditCompany?: (company: Company) => Promise<boolean>;
  handleDeleteCompany?: (company: Company) => Promise<boolean>;
  refetchData?: () => {};
}

const initialFormData: CreateCompanyData = {
  name: "",
  email: "",
  phone: "",
};

const initialErrors: Record<keyof CreateCompanyData, boolean> = {
  name: false,
  email: false,
  phone: false,
};

const CompanyDialogs: React.FC<DialogProps> = ({
  visible,
  onHide,
  dialogType,
  company,
  handleAddCompany,
  handleEditCompany,
  handleDeleteCompany,
  refetchData,
}) => {
  const [formData, setFormData] = useState<CreateCompanyData>(initialFormData);
  const [errors, setErrors] =
    useState<Record<keyof CreateCompanyData, boolean>>(initialErrors);
  const [validForm, setValidForm] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: keyof CreateCompanyData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const validateAllFields = (): boolean => {
    const newErrors: Record<keyof CreateCompanyData, boolean> = {
      name: !validateField("name", formData.name),
      email: !validateField("email", formData.email),
      phone: !validateField("phone", formData.phone),
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((err) => !err);
  };

  const handleBlur = (field: keyof CreateCompanyData) => {
    const value = formData[field];
    const isValid = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: !isValid }));
  };

  const handleSubmit = async () => {
    if (dialogType !== "delete")
      if (!validateAllFields()) {
        setValidForm(false);
        return;
      }

    setValidForm(true);
    setSubmitting(true);
    let success = false;
    try {
      if (dialogType === "create" && handleAddCompany) {
        success = await handleAddCompany!(formData);
      } else if (dialogType === "edit" && handleEditCompany) {
        success = await handleEditCompany!({ ...formData, id: company!.id });
      } else if (dialogType === "delete" && handleDeleteCompany) {
        success = await handleDeleteCompany!(company!);
      }
      if (success) {
        onHide();
        refetchData!();
      }
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (visible) {
      setValidForm(true);
      if (dialogType === "edit" && company) {
        const newData = {
          name: company.name || "",
          email: company.email || "",
          phone: company.phone
            ? String(company.phone).replace(/\D/g, "").slice(-10)
            : company.phone,
        };
        setFormData(newData);
        const newErrors: Record<keyof CreateCompanyData, boolean> = {
          name: !validateField("name", newData.name),
          email: !validateField("email", newData.email),
          phone: !validateField("phone", newData.phone),
        };
        setErrors(newErrors);
      } else {
        setFormData(initialFormData);
        setErrors(initialErrors);
      }
    }
  }, [visible, dialogType, company]);

  useEffect(() => {
    if (!visible) {
      setFormData(initialFormData);
      setErrors(initialErrors);
      setValidForm(true);
      setSubmitting(false);
    }
  }, [visible]);

  const getHeader = (type: string) => {
    switch (type) {
      case "create":
        return "Новая компания";
      case "edit":
        return "Редактировать компанию";
      case "delete":
        return "Удалить компанию";
      default:
        return "Ошибка загрузки данных";
    }
  };

  const footerContent = (
    <div>
      <Button label="Отмена" onClick={onHide} outlined />
      <Button
        label={
          dialogType === "delete"
            ? "Удалить"
            : dialogType === "edit"
            ? "Сохранить"
            : "Создать"
        }
        onClick={handleSubmit}
        loading={submitting}
      />
    </div>
  );

  const formContent = (
    <Column>
      {validForm ? (
        <></>
      ) : (
        <DangerText>Пожалуйста, заполните все поля данных.</DangerText>
      )}
      <CustomInputText
        value={formData.name}
        setValue={(val) => handleFieldChange("name", val)}
        onBlur={() => handleBlur("name")}
        placeholder="Имя"
        icon={<IconCompany size={20} />}
        invalid={errors.name}
      />
      <CustomInputText
        value={formData.email}
        setValue={(val) => handleFieldChange("email", val)}
        onBlur={() => handleBlur("email")}
        placeholder="Почта"
        icon={<IconMail size={20} />}
        invalid={errors.email}
      />
      <CustomInputMask
        value={formData.phone}
        setValue={(val) => handleFieldChange("phone", val)}
        onBlur={() => handleBlur("phone")}
        mask="+7 (999) 999-99-99"
        unmask={true}
        placeholder="+7 (___) ___-__-__"
        icon={<IconPhone size={20} />}
        invalid={errors.phone}
      />
    </Column>
  );

  const deleteForm = (
    <Column>
      <DangerText>Вы уверены, что хотите удалить данную компанию?</DangerText>
      <SmallColumn>
        <StyledIconLine
          icon={<IconCompanyFilled size={20} />}
          text={company?.name}
        />
        <StyledIconLine
          icon={<IconMailFilled size={20} />}
          text={company?.email ?? "Нет данных"}
        />
        <StyledIconLine
          icon={<IconPhoneFilled size={20} />}
          text={
            company
              ? formatPhone(company!.phone) == ""
                ? "Нет данных"
                : formatPhone(company!.phone)
              : "Нет данных"
          }
        />
      </SmallColumn>
    </Column>
  );

  const getContent = (type: string) => {
    switch (type) {
      case "create":
        return formContent;
      case "edit":
        return formContent;
      case "delete":
        return deleteForm;
      default:
        return <></>;
    }
  };

  return (
    <Dialog
      header={getHeader(dialogType)}
      visible={visible}
      style={{ width: "500px" }}
      onHide={onHide}
      footer={footerContent}
    >
      {getContent(dialogType)}
    </Dialog>
  );
};

export default CompanyDialogs;
